import { useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useMemoizedFn, useRequest, useSafeState } from 'ahooks';
import svgPanZoom from 'svg-pan-zoom';
import SVGTool from './svgTool';
import { Spin, Space } from 'antd';
import { EVENT_TYPES } from './types';
import Empty from '../../Empty';
import zoomInIcon from '@/pages/RepairInfoSearch/assets/zoomin.png';
import zoomOutIcon from '@/pages/RepairInfoSearch/assets/zoomout.png';
import resetIcon from '@/pages/RepairInfoSearch/assets/reset.png';
import { useTranslation } from '@/hooks/useTranslation';

import styles from './index.module.less';

type SvgPanelProps = {
  svgUrl: string;
  setSelectPCode: (pcode: string) => void;
  selectPCode: string;
  defaultPCode: string;
  fill: boolean;
  height: number;
  paramMaps?: Record<string, string>; // 参数定义列表
  validPCodeList: string[]; // 可以点击的pcode列表
};

const SvgPanel = (props: SvgPanelProps) => {
  const $ = useTranslation();
  const { svgUrl, setSelectPCode, selectPCode, defaultPCode, paramMaps, validPCodeList } = props;
  const svgContainerRef = useRef<HTMLDivElement | null>(null);
  const svgWrapRef = useRef<HTMLDivElement | null>(null);
  const panZoomInstance = useRef<SvgPanZoom.Instance | null>(null);
  const svgToolInstance = useRef<SVGTool | null>(null);
  const [svgContent, setSvgContent] = useSafeState<string | null>(null);

  // 动态加载 SVG 文件
  const { loading } = useRequest(
    async () => {
      if (!svgUrl) throw new Error('svgUrl is required');
      const response = await fetch(svgUrl);
      const svgText = await response.text();
      const svgTagMatch = svgText.match(/<svg[^>]*>[\s\S]*<\/svg>/i);
      if (response.status === 200 && svgTagMatch) {
        setSvgContent(svgTagMatch[0]);
      } else throw new Error('SVG file not found or invalid');
    },
    {
      refreshDeps: [svgUrl],
      onError: () => {
        setSvgContent(null);
        if (svgWrapRef.current)
          svgWrapRef.current.innerHTML = ReactDOMServer.renderToString(
            <Empty description={$('该电路图未上传SVG图片，请联系对应中台人员补充')} />
          );
      }
    }
  );

  const onHotSpotClick = useMemoizedFn((text: string) => {
    setSelectPCode(text);
  });

  const destroyToolAndInstance = useMemoizedFn(() => {
    try {
      if (panZoomInstance.current) {
        const svgElement = svgWrapRef.current?.shadowRoot?.querySelector('svg');
        if (svgElement) panZoomInstance.current.destroy();
        panZoomInstance.current = null;
      }
      if (svgWrapRef.current) {
        if (svgWrapRef.current.shadowRoot) svgWrapRef.current.shadowRoot.innerHTML = '';
        else svgWrapRef.current.innerHTML = '';
      }
      svgToolInstance.current?.destroy();
      svgToolInstance.current = null;
    } catch (e) {
      console.log('destroyToolAndInstance error:', e);
    }
  });

  const initInstance = useMemoizedFn(() => {
    if (svgWrapRef.current) {
      if (svgContent) {
        // 检查是否已经有 Shadow DOM(样式隔离)
        if (!svgWrapRef.current.shadowRoot) {
          svgWrapRef.current.attachShadow({ mode: 'open' });
        }
        const shadowRoot = svgWrapRef.current.shadowRoot;
        if (!shadowRoot) return;
        shadowRoot.innerHTML = `
          <style>
            svg {
              width: 100%;
              min-width: 100%;
              height: 100%;
            }
          </style>
          ${svgContent}
        `;
        const svgElement = shadowRoot.querySelector('svg');
        if (svgElement) {
          panZoomInstance.current = svgPanZoom(svgElement, {
            zoomEnabled: true,
            controlIconsEnabled: false,
            maxZoom: 30,
            center: true,
            fit: true
          });
          svgToolInstance.current?.destroy();
          svgToolInstance.current = new SVGTool({
            svgElement,
            defaultPCode: defaultPCode,
            paramMaps: paramMaps ?? {},
            validPCodeList
          });
          svgToolInstance.current.subscribe(EVENT_TYPES.TEXTCLICK, onHotSpotClick);
        }
      } else {
        svgWrapRef.current.innerHTML = ReactDOMServer.renderToString(
          <Empty
            description={
              svgUrl && loading
                ? $('加载中...')
                : $('该电路图未上传SVG图片，请联系对应中台人员补充')
            }
          />
        );
      }
    }
  });

  useEffect(() => {
    destroyToolAndInstance();
    initInstance();
    return () => {
      destroyToolAndInstance();
    };
  }, [svgContent, initInstance, destroyToolAndInstance]);

  useEffect(() => {
    if (selectPCode) {
      svgToolInstance.current?.outsideHighlight(selectPCode);
    }
  }, [selectPCode]);

  const handleZoomIn = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.zoomIn();
    }
  });

  const handleZoomOut = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.zoomOut();
    }
  });

  const handleReset = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.resetZoom();
      panZoomInstance.current.resetPan();
    }
  });

  // 监听窗口变化，调整 SVG 尺寸
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        // 注意：当元素隐藏/显示切换时也会被监测，只需在元素显示时才执行
        if (entry.contentRect.width && panZoomInstance.current) {
          try {
            panZoomInstance.current.resize();
            panZoomInstance.current.resetZoom();
            panZoomInstance.current.resetPan();
          } catch (e) {
            initInstance();
          }
        }
      });
    });

    if (svgContainerRef.current) resizeObserver.observe(svgContainerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [initInstance]);

  return (
    <div
      className={`${styles.svgContainer}`}
      style={{ height: props.fill ? props.height - 60 : props.height - 300 }}
      ref={svgContainerRef}
    >
      <Spin spinning={loading}>
        <Space className={styles.controls} size={16}>
          <img src={zoomInIcon} alt='zoom-in' onClick={handleZoomIn} />
          <img src={zoomOutIcon} alt='zoom-out' onClick={handleZoomOut} />
          <img src={resetIcon} alt='reset' onClick={handleReset} />
        </Space>
        <div className={styles.svgWrap} ref={svgWrapRef}>
          <Empty description={$('加载中...')} />
        </div>
      </Spin>
    </div>
  );
};

export default SvgPanel;
